import React from 'react'
import {
  TableHead,
  TableRow,
  TableCell,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
} from '@mui/material'
import { useSearch } from '../../hooks/useSearch'
import { getWittenMessages } from '../../store/chatsWritten/actions'
import { STATUSES_WRITTEN } from '../../utils/consts'
import { useSelector } from 'react-redux'
import { IStore } from '../../store/types'
import { BasicDatePicker } from '../../ui/BasicDatePicker'
import moment from 'moment'
import { useNavigate, useParams } from 'react-router-dom'
import { useAppDispatch } from '../../store'
import { checkFio } from '../../utils/checkFio'
import { SelectCheckboxSearch } from '../../ui/SelectCheckboxSearch'
import { callApi } from '../../utils/callApi'
import { IProduct } from '../../store/products/types'
import { buildWrittenProductOptions, getWrittenProductSelectLabel } from './productOptions'

interface Props {
  search: string
  handleChangeSearch: (value: string) => void
}

export const TableHeadWritten: React.FC<Props> = ({ search, handleChangeSearch }) => {
  const [messageId, setMessageId] = React.useState('')
  const [email, setEmail] = React.useState('')
  const [expertIds, setExpertIds] = React.useState<string[]>([])
  const [experts, setExperts] = React.useState<{ value: string; label: string }[]>([])
  const [product_id, setProductId] = React.useState('')
  const [productIdsFilter, setProductIdsFilter] = React.useState('')
  const [writtenProducts, setWrittenProducts] = React.useState<IProduct[] | null>(null)
  const [status, setStatus] = React.useState<string[]>([])
  const [originFilter, setOriginFilter] = React.useState('')
  const [dateStart, setDateStart] = React.useState<Date | null | undefined>(null)
  const [dateEnd, setDateEnd] = React.useState<Date | null | undefined>(null)

  const { chatsWritten, employees } = useSelector((store: IStore) => store)

  const { handleSearch } = useSearch({ actionThunk: getWittenMessages })
  const productOptions = React.useMemo(() => buildWrittenProductOptions(writtenProducts), [writtenProducts])
  const dispatch = useAppDispatch()
  const { id } = useParams()
  const navigate = useNavigate()

  const obj = {
    page: String(1),
    message_id: messageId.split('/')[0],
    part_id: messageId.split('/')[1],
    status: Object.keys(STATUSES_WRITTEN)
      .filter((it) => status.includes(STATUSES_WRITTEN[it].title))
      .join(','),
    product_id: product_id.startsWith('all:') ? '' : product_id,
    expert_id:
      experts
        ?.filter((it) => expertIds.includes(it.label))
        .map((it) => it.value)
        .join(',') || '',
    email,
    product_ids_filter: productIdsFilter,
    date_start: dateStart ? moment(dateStart).format('YYYY-MM-DD HH:mm') : '',
    date_end: dateEnd ? moment(dateEnd).format('YYYY-MM-DD HH:mm') : '',
    question: search,
    origin_filter: originFilter,
  }

  React.useEffect(() => {
    let isMounted = true

    callApi({
      method: 'get',
      path: '/admin-office/products?with_written_dialogs=1',
    })
      .then((products) => {
        if (isMounted) setWrittenProducts(products as IProduct[])
      })
      .catch(() => {
        if (isMounted) setWrittenProducts([])
      })

    return () => {
      isMounted = false
    }
  }, [])

  React.useEffect(() => {
    const obj = {} as { message_id?: number }
    if (id && !Number.isNaN(Number(id))) {
      obj['message_id'] = Number(id)
      setMessageId(id)
    }
    dispatch(getWittenMessages(obj))
  }, [id])

  React.useEffect(() => {
    if (chatsWritten.page) {
      let message_id = ''
      if (id && !Number.isNaN(Number(id))) {
        message_id = id
        setMessageId(id)
        navigate('/letter')
      }
      handleSearch(String(chatsWritten.page), 'page', { ...obj, message_id })
    }
  }, [chatsWritten.page])

  React.useEffect(() => {
    if (employees.data) {
      setExperts(employees.data.employees.map((item) => ({ value: String(item.id), label: checkFio(item) })))
    }
  }, [employees.data])

  const handleClearFilters = () => {
    setMessageId('')
    setEmail('')
    setExpertIds([])
    setProductId('')
    setProductIdsFilter('')
    setStatus([])
    setOriginFilter('')
    setDateStart(null)
    setDateEnd(null)
    handleChangeSearch('')
    dispatch(getWittenMessages({}))
  }

  return (
    <TableHead>
      <TableRow>
        <TableCell sx={{ fontWeight: 'bold' }} align={'center'}>
          № / Час створення / Джерело
          <TextField
            style={{ width: '150px', marginTop: '10px' }}
            size={'small'}
            id='outlined-basic-id-messageId'
            label='№'
            variant='outlined'
            value={messageId}
            onChange={(event) => {
              setMessageId(event.target.value)
              const ids = event.target.value.split('/')
              handleSearch(ids[0], 'message_id', { ...obj, part_id: ids[1] })
            }}
          />
          <BasicDatePicker
            style={{ width: '150px', marginTop: '10px' }}
            id={'date_start'}
            label='з'
            value={dateStart}
            onChange={(date) => {
              setDateStart(date)
              const date_start = date ? moment(date).format('YYYY-MM-DD HH:mm') : ''
              if (!date_start || moment(date_start).isValid()) {
                handleSearch(date_start, 'date_start', obj)
              }
            }}
          />
          <BasicDatePicker
            style={{ width: '150px', marginTop: '10px' }}
            id={'date_end'}
            label='по'
            value={dateEnd}
            onChange={(date) => {
              setDateEnd(date)
              const date_end = date ? moment(date).format('YYYY-MM-DD HH:mm') : ''
              if (!date_end || moment(date_end).isValid()) {
                handleSearch(date_end, 'date_end', obj)
              }
            }}
          />
          <FormControl sx={{ width: '150px', marginTop: '10px' }}>
            <InputLabel size={'small'} id='simple-select-origin'>
              Джерело запитання
            </InputLabel>
            <Select
              size={'small'}
              labelId='simple-select-origin'
              id='simple-select-origin'
              label='Джерело запитання'
              value={originFilter}
              onChange={(e) => {
                setOriginFilter(e.target.value)
                handleSearch((e.target.value as string) || '', 'origin_filter', obj)
              }}
            >
              <MenuItem value={''}>Усі</MenuItem>
              <MenuItem value={'site'}>Сайт</MenuItem>
              <MenuItem value={'online_all'}>Передано з Онлайн (Pro+Standart)</MenuItem>
              <MenuItem value={'online_pro'}>Передано з Онлайн тариф Pro</MenuItem>
              <MenuItem value={'online_standart'}>Передано з Онлайн тариф Standart</MenuItem>
              <MenuItem value={'tech_support'}>Передано з Техпідтримки</MenuItem>
            </Select>
          </FormControl>
        </TableCell>
        <TableCell sx={{ fontWeight: 'bold' }} align={'center'}>
          Дедлайн
          <hr />
          Відредаговано
        </TableCell>
        <TableCell sx={{ fontWeight: 'bold' }} align={'center'}>
          Контакти особи, що звернулася
          <TextField
            style={{ width: '150px', marginTop: '20px' }}
            size={'small'}
            id='outlined-basic-id-1'
            label='Email'
            variant='outlined'
            value={email}
            onChange={(event) => {
              setEmail(event.target.value)
              handleSearch(event.target.value, 'email', obj)
            }}
          />
        </TableCell>
        <TableCell sx={{ minWidth: '350px', fontWeight: 'bold' }} align={'center'}>
          Запитання
          <TextField
            style={{ width: '250px', marginTop: '20px' }}
            size={'small'}
            id='outlined-basic-id-question'
            label='Запитання'
            variant='outlined'
            value={search}
            onChange={(event) => {
              const value = event.target.value
              if (value.length <= 70) {
                handleChangeSearch(value)
                handleSearch(value, 'question', obj)
              }
            }}
          />
        </TableCell>
        <TableCell sx={{ fontWeight: 'bold' }} align={'center'}>
          Система / Видання
          <FormControl sx={{ width: '150px', marginTop: '20px' }}>
            <InputLabel size={'small'} id='simple-select' shrink>
              Продукт
            </InputLabel>
            <Select
              size={'small'}
              displayEmpty
              labelId='simple-select'
              id='simple-select'
              label='Продукт'
              value={product_id}
              renderValue={(value) => getWrittenProductSelectLabel(value as string, productOptions)}
              onChange={(e) => {
                const value = e.target.value as string
                const option = productOptions.find((item) => item.value === value)

                setProductId(value)

                if (option?.type === 'all') {
                  const ids = option.productIds.join(',')
                  setProductIdsFilter(ids)
                  handleSearch(ids, 'product_ids_filter', { ...obj, product_id: '', product_ids_filter: ids })
                } else {
                  setProductIdsFilter('')
                  handleSearch(value || '', 'product_id', { ...obj, product_id: value, product_ids_filter: '' })
                }
              }}
            >
              <MenuItem value={''}>Усі</MenuItem>
              {productOptions.map((item) => (
                <MenuItem key={`${item.type}-${item.value}`} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </TableCell>
        <TableCell sx={{ fontWeight: 'bold', zIndex: 4 }} align={'center'}>
          Експерт
          <SelectCheckboxSearch
            style={{ width: '150px', marginTop: '20px' }}
            label='Експерт'
            value={expertIds}
            options={employees.data?.employees.map((item) => ({ value: String(item.id), label: checkFio(item) })) || []}
            onChange={(value) => {
              setExpertIds(value)
              handleSearch(
                experts
                  ?.filter((it) => value.includes(it.label))
                  .map((it) => it.value)
                  .join(',') || '',
                'expert_id',
                obj
              )
            }}
          />
        </TableCell>
        <TableCell sx={{ fontWeight: 'bold', zIndex: 3 }} align={'center'}>
          Статус
          <SelectCheckboxSearch
            style={{ width: '150px', marginTop: '20px' }}
            value={status}
            label='Статус'
            options={
              Object.keys(STATUSES_WRITTEN).map((item) => ({ value: item, label: STATUSES_WRITTEN[item].title })) || []
            }
            onChange={(value) => {
              setStatus(value)
              handleSearch(
                Object.keys(STATUSES_WRITTEN)
                  .filter((it) => value.includes(STATUSES_WRITTEN[it].title))
                  .join(','),
                'status',
                obj
              )
            }}
          />
        </TableCell>
        <TableCell sx={{ fontWeight: 'bold' }} align={'center'}>
          Додатково
          <Button sx={{ mt: 2 }} size='small' color={'info'} variant={'contained'} onClick={handleClearFilters}>
            Очистити фільтри
          </Button>
        </TableCell>
      </TableRow>
    </TableHead>
  )
}
